LS.util = {
	/**
	 * Execute when idle
	 *
	 * @param {Array|Function} fn
	 * @param {Object} [context]
	 * @returns mixed
	 */
	idle(fn, context) {
		const idle = 'requestIdleCallback' in window;

		$.toArray(fn).forEach(el => {
			(idle ? requestIdleCallback : setTimeout)(() => {
				el.apply(context);
			});
		});
	},

	/**
	 * Focus element
	 *
	 * @param {element} [el]
	 */
	focus(el) {
		if (el) {
			el.focus({
				focusVisible: false,
				preventScroll: true
			});
		}
	},

	/**
	 * Reset all namespaced events and dependents
	 *
	 * @param {String} namespace
	 * @param {Object} [context]
	 * @param {Array|String} [instances]
	 * @param {Boolean} [observables=false]
	 */
	reset(namespace, context, instances, observables) {
		$.scroll.reset(namespace);
		$.screen.reset(namespace);
		$.events.reset(namespace);

		if (observables) {
			$.unobserve('*', {
				namespace: namespace
			});
		}

		if (instances) {
			LS.util.destroy(context, instances);
		}
	},

	/**
	 * Destroy scoped instances
	 *
	 * @param {Object} context
	 * @param {Array|String} instances
	 */
	destroy(context, instances) {
		$.toArray(instances).forEach(name => {
			if (context[name]) {
				context[name].$destroy();

				context[name] = null;

				delete context[name];
			}
		});
	},

	/**
	 * Display flash message
	 *
	 * @param {String} value
	 * @param {String} [status='info']
	 * @param {Object} [options]
	 */
	flash(value, status, options) {
		LS.util.load('flash', () => {
			LS.flash.show(value, status, options);
		});
	},

	/**
	 * Display flash message
	 *
	 * @param {String} value
	 */
	warn(value) {
		LS.util.flash(value, 'warning');

		if (! LS.flash && ! navigator.onLine) {
			alert(value);
		}
	},

	/**
	 * Capitalize first letter of string
	 *
	 * @param {String} value
	 * @returns {String}
	 */
	capitalize(value) {
		return value.charAt(0).toUpperCase() + value.slice(1);
	},

	/**
	 * Parse value into number
	 *
	 * @param {Number|String} value
	 * @returns {*}
	 */
	parseNumber(value) {
		if (value && typeof value === 'string') {
			return parseFloat(
				value.replace(/[^\d.]/g, '')
			);
		}

		return value;
	},

	/**
	 * Round number to specified decimal places
	 *
	 * @param {Number} number
	 * @param {Number} [decimals=0]
	 * @returns {Number}
	 */
	round(number, decimals = 0) {
		return Number(
			Math.round(number + 'e' + decimals) + 'e-' + decimals
		);
	},

	/**
	 * Format currency
	 *
	 * @param {Number|String} value
	 * @param {Boolean} [decimals=false]
	 * @returns {*}
	 */
	formatCurrency(value, decimals) {
		let formatted = LS.util.formatNumber(value, decimals ? 2 : 0);

		if (decimals && formatted) {
			const parts = formatted.split('.');

			if (parts.length > 1 && parts[1].length === 1) {
				formatted += '0';
			}
		}

		return formatted || formatted === 0 ?
			'$' + formatted : value;
	},

	/**
	 * Format number with thousands separators
	 *
	 * @param {Number|String} value
	 * @param {Boolean|Number} [decimals=0]
	 * @param {Boolean} [trim=false]
	 * @returns {*}
	 */
	formatNumber(value, decimals = 0, trim) {
		if (value) {
			let parsed = LS.util.parseNumber(value);

			if (isNaN(parsed)) {
				return value;
			}

			if (decimals !== true) {
				parsed = LS.util.round(parsed, decimals);
			}

			if (trim) {
				parsed = parseFloat(parsed);
			} else if (decimals === true) {
				const match = value.toString()
					.match(/\.(\d*?(0+))$/);

				if (match) {
					parsed += match[1] === match[2] ?
						match[0] : match[2];
				}
			}

			parsed = parsed.toString()
				.split('.');

			if (parsed[0] >= 1000) {
				parsed[0] = parsed[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
			}

			return parsed.join('.');
		}

		return value;
	},

	/**
	 * Format a range of filter values
	 *
	 * @param {Object} value
	 * @param {String} [prefix]
	 * @param {String} [suffix]
	 * @param {String} [fallback]
	 * @returns {String}
	 */
	formatRange(value, prefix, suffix, fallback) {
		let output = fallback;

		if (! value) {
			return output;
		}

		let min = value.min && value.min > 0 ?
			LS.util.shortFormat(value.min) : false;
		let max = value.max && value.max > 0 ?
			LS.util.shortFormat(value.max) : false;
		let plural = true;

		if (! min && ! max) {
			return output;
		}

		if (min && max) {
			if (min === max) {
				output = min;

				if (value.min === 1) {
					plural = false;
				}
			} else {
				if (parseFloat(value.min) > parseFloat(value.max)) {
					const temp = min;

					min = max;
					max = temp;
				}

				output = min + '-' + max;
			}
		} else if (min) {
			output = min + '+';

			if (value.min === 1) {
				plural = false;
			}
		} else if (max) {
			output = '0-' + max;

			if (value.max === 1) {
				plural = false;
			}
		}

		if (suffix) {
			if (plural) {
				suffix += 's';
			}

			output += ' ' + suffix;
		}

		if (prefix) {
			output = '$' + output;
		}

		return output;
	},

	/**
	 * Format property size for display
	 *
	 * @param {Number|String} value
	 * @param {Boolean} [round=false]
	 * @param {Boolean} [abbreviate=false]
	 * @param {Boolean} [suffix=true]
	 * @returns {String}
	 */
	formatSize(value, round, abbreviate, suffix = true) {
		if (! value) {
			return '—';
		}

		let raw = value;

		if (round) {
			const diff = Math.floor((raw * 1000) % 100);

			if (raw <= 1) {
				value = LS.util.round(raw, raw < 0.1 ? 3 : (diff < 10 ? 1 : 2));
			} else if (raw < 1000) {
				value = Math.round(raw);

				if (raw < 100 && Math.abs(value - raw).toFixed(2) >= 0.1) {
					value = LS.util.round(raw, raw > 10 || diff <= 10 ? 1 : 2);
				}
			}
		}

		raw = value = parseFloat(value);

		if (value >= 1000) {
			value = LS.util.formatNumber(value);
		}

		if (! suffix) {
			return value;
		}

		if (abbreviate) {
			return value + ' ac';
		}

		return value + ' ' + LS.util.plural('acre', raw);
	},

	/**
	 * Format number with abbreviated syntax
	 *
	 * @param {Number|String} value
	 * @param {Number} [decimals]
	 * @returns {String}
	 */
	shortFormat(value, decimals) {
		if (value) {
			let num = value.toString()
				.replace(/[^\d.-]/g, '');

			if (num >= 1000) {
				const base = num < 999500 ? 1 : Math.floor(
					(Math.log(Math.abs(num)) / Math.log(1000)) + 0.05
				);

				num = (num / Math.pow(1000, base));

				if (num >= 100) {
					num = Math.round(num);
				} else {
					num = LS.util.round(num, decimals || (num < 2.5 ? 2 : 1));
				}

				return parseFloat(num).toString() +
					('kmb'[base - 1] || '');
			}

			if (decimals) {
				return LS.util.formatNumber(value, decimals, true);
			}
		}

		return isNaN(value) ?
			value : parseFloat(value).toString();
	},

	/**
	 * Evaluate value
	 *
	 * @param {Function|String} value
	 * @param {*} [data]
	 */
	val(value, data) {
		return typeof value === 'function' ?
			value(data) : value;
	},

	/**
	 * Restrict input to only numerical values
	 *
	 * @param {$} sel
	 * @param {Function} [fn]
	 * @param {String} [namespace]
	 * @param {$} [delegate]
	 */
	restrict(sel, fn, namespace, delegate) {
		const allowedKeys = [
			0, 8, 9, 13, 46, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57
		];

		let before;
		let count;
		let pos;
		let valid;
		let value;
		let first;
		let last;

		$.events.on(sel, 'keypress', (e, el) => {
			if (! e.ctrlKey) {
				if (! allowedKeys.includes(e.keyCode || e.which)) {
					e.preventDefault();
				} else {
					valid = true;
				}
			}

			before = el.value;
		}, {
			delegate: delegate,
			namespace: namespace
		});

		$.events.on(sel, 'keyup', (e, el) => {
			if (window.getSelection().toString()) {
				return;
			}

			pos = el.selectionStart;
			value = el.value;
			first = value[0];
			last = value.slice(-1);

			if (first === '$') {
				value[1] ?
					first = value[1] :
					el.value = '';
			}

			if (last === '.') {
				return;
			}

			count = el.value.length;

			el.value = el.pattern.includes(',') ?
				LS.util.formatNumber(value, true) : value;

			if (fn && (valid || e.keyCode === 8)) {
				fn(el);
			}

			if (before === el.value) {
				return;
			}

			before = el.value;

			el.selectionEnd = Math.max(pos + (el.value.length - count), 0);
		}, {
			delegate: delegate,
			namespace: namespace
		});
	},

	/**
	 * Conditionally make word plural
	 *
	 * @param {String} label
	 * @param {Number} num
	 * @returns {String}
	 */
	plural(label, num) {
		if (num === 1) {
			return label;
		}

		if (label.slice(-1) === 'y') {
			return label.slice(0, -1) + 'ies';
		}

		return label + 's';
	},

	/**
	 * Get pagination offset value
	 *
	 * @param {Number} pageSize
	 * @returns {Number}
	 */
	getOffset(pageSize) {
		return (($.routes.segments(1).slice(1) || 1) - 1) * pageSize;
	},

	/**
	 * Remove object entries with empty values
	 *
	 * @param {Object} data
	 * @returns {Object}
	 */
	sanitize(data) {
		for (const [key, value] of Object.entries(data)) {
			if (! value || (Array.isArray(value) && ! value.length)) {
				delete data[key];
			}
		}

		return data;
	},

	/**
	 * Parse object template over iterable data
	 *
	 * @param {Array} data
	 * @param {Object} base
	 * @param {Object} extend
	 * @returns {Array}
	 */
	transform(data, base, extend) {
		return data.map(el => {
			const obj = {};

			Object.keys(extend).forEach(key => {
				obj[key] = el[key];
			});

			return Object.assign({}, base, obj);
		});
	},

	/**
	 * Return keyed values from array as new array
	 *
	 * @param {Array} data
	 * @param {String} key
	 * @returns {Array}
	 */
	pluck(data, key) {
		const segs = key.split('.');
		const nested = segs.length > 1;
		const response = [];

		if (! data || ! data.length) {
			return response;
		}

		if (! nested) {
			return data.map(el => el[key]);
		}

		data.forEach(el => {
			segs.forEach(seg => {
				el = el[seg];
			});

			response.push(el);
		});

		return response;
	},

	/**
	 * Transform array of objects into keyed object
	 *
	 * @param {Array} data
	 * @param {String} [key=id]
	 * @returns {Object}
	 */
	key(data, key = 'id') {
		const response = {};

		if (! data) {
			return response;
		}

		data.forEach(item => {
			response[item[key]] = item;
		});

		return response;
	},

	/**
	 * Output ISO date in human-friendly format
	 *
	 * @param value
	 * @param {Boolean} [time=false]
	 * @param {Boolean} [year=true]
	 * @returns {String}
	 */
	dateFormat(value, time, year = true) {
		const date = new Date(value);
		const months = [
			'Jan', 'Feb', 'Mar', 'Apr', 'May', 'June',
			'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
		];

		let formatted = months[date.getMonth()] +
			' ' + date.getDate();

		if (year) {
			formatted += ', ' + date.getFullYear();
		}

		if (time) {
			const hours = date.getHours();
			const minutes = date.getMinutes();
			const period = hours < 12 ?
				'am' : 'pm';

			formatted += ' ' + (
				period === 'am' ?
					(hours || 12) : (
						hours === 12 ?
							12 : hours - 12
					)
				) + ':' + (
					minutes < 10 ?
						'0' + minutes : minutes
				) + ' ' + period;
		}

		return formatted;
	},

	/**
	 * Format relative age
	 */
	dayFormat(value) {
		let suffix = 'day';

		if (isNaN(value)) {
			value = Math.ceil(
				(new Date().getTime() - new Date(value).getTime()) / 60000
			);
		}

		if (value > 1051898) {
			value = Math.floor(value / 525949);
			suffix = 'year';
		} else if (value > 87658) {
			value = Math.floor(value / 43829);
			suffix = 'month';
		} else if (value < 60) {
			suffix = 'min';
		} else if (value < 1440) {
			value = Math.floor(value / 60);
			suffix = 'hour';
		} else {
			value = Math.floor(value / 1440);
		}

		if (value !== 1) {
			suffix += 's';
		}

		return value + ' ' + suffix;
	},

	/**
	 * Localize timezone
	 *
	 * @param {Date|String} date
	 * @param {Object} [options]
	 * @return Date
	 */
	localize(date, options) {
		return new Date(
			new Date(date).toLocaleString('en-US', options)
		);
	},

	/**
	 * General unique ID
	 *
	 * @returns {String}
	 */
	uid() {
		return new Date().getTime() + Math.random()
			.toString(36)
			.slice(2);
	},

	/**
	 * Parse markdown
	 *
	 * @param {String} val
	 * @returns {String}
	 */
	markdown(val) {
		if (! val) {
			return '';
		}

		const methods = {
			b: (text, hash, line) => {
				if (line.length > 80) {
					return line;
				}

				return line.trim() ?
					`<b>${line}</b>` : ' ';
			},

			h: (text, hash, line) => {
				line = line.trim();

				if (line.length > 50) {
					return `\n<p>${line}</p>`;
				}

				return line ?
					`\n<h3>${line}</h3>` : '';
			},

			p: (text, line) => {
				line = line.trim();

				if (/^<\/?(ul|ol|li|p|h\d)/i.test(line)) {
					return line;
				}

				return line ?
					`\n<p>${line}</p>` : '';
			},

			u: text => {
				return text.replace(/\n\n[*+·•-]([^\n*-].+)/g, (text, line) => {
					line = line.trim();

					return line ?
						`\n<ul><li>${line}</li></ul>` : '';
				});
			},

			o: text => {
				let seq = true;
				let num = 0;

				const list = text.replace(/\n\n(\d{1,2})\.?(.+)/g, (text, pre, line) => {
					num++;

					if (Number(pre) !== num) {
						seq = false;

						return;
					}

					line = line.trim();

					return line ?
						`\n<ol><li>${line}</li></ol>` : '';
				});

				return seq ?
					list : text;
			}
		};

		const rules = [
			[
				/\\n/g,
				'\n'
			],
			[
				/ {2,}/g,
				' '
			],
			[
				/\n/g,
				'\n\n'
			],
			[
				/\n{2,}/g,
				'\n\n'
			],
			[
				/(\*\*|__)(.*?)\1/g,
				methods.b
			],
			[
				/\n\n(#+)(.*)/g,
				methods.h
			],
			[
				/(\n\n[*+].+){2,}/g,
				methods.u
			],
			[
				/(\n\n[·•-].+)+/g,
				methods.u
			],
			[
				/(\n\n(\d{1,2}\.?) .+){3,}/g,
				methods.o
			],
			[
				/\n([^\n]+)\n/g,
				methods.p
			],
			[
				/<p>.<\/p>/g,
				''
			],
			[
				/\s?<\/ul>\s?<ul>/g,
				''
			],
			[
				/\s?<\/ol>\s?<ol>/g,
				''
			],
			[
				/\\\*/g,
				'*'
			]
		];

		val = `\n${val}\n`;

		rules.forEach(rule => {
			val = val.replace(rule[0], rule[1]);
		});

		return val.replace('/[\r\n]+/g', '')
			.replace(/<ul>/g, '<ul class="bulleted">')
			.replace(/<ol>/g, '<ol class="numbered">')
			.trim();
	},

	/**
	 * Convert X coordinate from longitude
	 *
	 * @param {Number} longitude
	 * @param {Number} [zoom=15]
	 * @returns {Number}
	 */
	getTileX(longitude, zoom = 15) {
		return (Math.floor((longitude + 180) / 360 * Math.pow(2, zoom)));
	},

	/**
	 * Convert Y coordinate to latitude
	 *
	 * @param {Number} latitude
	 * @param {Number} [zoom=15]
	 * @returns {Number}
	 */
	getTileY(latitude, zoom = 15) {
		return (Math.floor((1 - Math.log(Math.tan(latitude * Math.PI / 180) + 1 /
			Math.cos(latitude * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom)));
	},

	/**
	 * Crop body
	 */
	crop() {
		if (LS.state.cropped !== false) {
			return;
		}

		const offset = window.scrollY;
		const padding = window.innerWidth - document.body.clientWidth;

		document.body.style.top = (offset * -1) + 'px';

		if (padding) {
			document.body.style.paddingRight = padding + 'px';
		}

		document.body.classList.add('-is-cropped');

		LS.state.cropped = offset;
	},

	/**
	 * Uncrop body
	 */
	uncrop() {
		if (
			LS.state.cropped === false ||
			Apt.panel.opened()
		) {
			return;
		}

		document.body.classList.remove('-is-cropped');

		document.body.removeAttribute('style');

		document.documentElement.scrollTop = LS.state.cropped;

		LS.state.cropped = false;
	},

	/**
	 * Load modules
	 *
	 * @param {Array|String} modules
	 * @param {Function} [fn]
	 * @param {Boolean} [script=true]
	 * @param {Boolean} [style=true]
	 */
	load(modules, fn, script = true, style = true) {
		modules = $.toArray(modules);

		let pending = modules.length;

		const loaded = () => {
			pending--;

			if (fn && ! pending) {
				fn();
			}
		};

		modules.forEach(name => {
			if (Apt[name]) {
				loaded();

				return;
			}

			const version = $.get('versions.' + name);
			const path = `/assets/modules/${name}@${version}`;
			const conf = {
				async: true,
				success: loaded
			};

			if (style) {
				conf.css = path + '/style.min.css';
			}

			if (script) {
				conf.js = path + '/script.min.js';
			}

			$.assets.load(conf);
		});
	},

	/**
	 * Run pending routes
	 *
	 * @param {Object} routes
	 */
	route(routes) {
		$.routes.map(routes);

		if (
			$.routes.uri().history &&
			! $.history.get(true)
		) {
			$.routes.run({
				routes: routes
			});
		}
	},

	/**
	 * Calculate position for scroll target
	 *
	 * @param {$} target
	 * @param {Number} [offset=20]
	 * @returns {Number}
	 */
	getScroll(target, offset) {
		const $target = $(target);

		if (! $target.length) {
			return 0;
		}

		const paneled = Apt.panel.opened();

		let position = $.offset($target).top - (
			isNaN(offset) ?
				(paneled ? 88 : 18) : offset
		);

		position -= paneled ?
			$.offset('$panel').top - $$('panelInner').scrollTop() : 70;

		return position < 70 ?
			0 : position;
	},

	/**
	 * Tween to the specified scroll position
	 *
	 * @param {$} target
	 * @param {Boolean} [conditional=false]
	 * @param {Number} [offset=20]
	 * @param {Boolean} [animate=true]
	 */
	setScroll(target, conditional, offset, animate = true) {
		const $target = $(target);

		if (! $target.length) {
			return;
		}

		if (conditional && $target.visible()) {
			return
		}

		LS.util.scrollTarget()
			.scrollTop(
				LS.util.getScroll($target, offset), animate
			);
	},

	/**
	 * Get scroll target
	 *
	 * @returns {$}
	 */
	scrollTarget() {
		return $(
			Apt.panel.opened() ?
				'$panelInner' : document.scrollingElement
		);
	},

	/**
	 * Execute callback based on scroll position
	 *
	 * @param {$} target
	 * @param {String} namespace
	 * @param {Function} fn
	 * @param {Object} [options]
	 */
	scrolled(target, namespace, fn, options) {
		$.scroll.map(Object.assign({
			context: Apt.panel.opened() ?
				'$panelInner' : null,
			target: target,
			namespace: namespace,
			once: true,
			callback: fn
		}, options));
	},

	/**
	 * Convert the current value to slug format
	 *
	 * @param {String} val
	 * @returns {String}
	 */
	slugify(val) {
		return val.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.replace(/[^a-z\d_\s\-\/]/g, '')
			.trim()
			.replace(/[\s\-\/_]+/g, '-');
	},

	/**
	 * Prefetch assets
	 *
	 * @param {Array} links
	 */
	prefetch(links) {
		links.forEach(href => {
			if (LS.stash.fetch[href]) {
				return;
			}

			LS.stash.fetch[href] = true;

			try {
				const link = document.createElement('link');

				if (link.relList.supports('prefetch')) {
					link.rel = 'prefetch';
					link.href = href;

					document.head.appendChild(link);

					return;
				}
			} catch (e) {
				//
			}

			LS.util.idle(() => {
				const xhr = new XMLHttpRequest();

				xhr.open('GET', href, true);
				xhr.send();
			});
		});
	},

	/**
	 * Get closest value from array
	 *
	 * @param {Number} val
	 * @param {Array} arr
	 * @returns {Number}
	 */
	closest(val, arr) {
		return Math.max.apply(null, arr.filter(item => item <= val));
	},

	/**
	 * Build breadcrumb array
	 *
	 * @param {String} root
	 * @param {Object} [loc]
	 * @returns {Array}
	 */
	breadcrumb(root, loc) {
		const breadcrumbs = [];

		if (! loc) {
			return breadcrumbs;
		}

		if (loc.territory) {
			breadcrumbs.push([
				loc.territory.name,
				root + '/' + loc.territory.slug
			]);
		}

		if (loc.region) {
			breadcrumbs.push([
				loc.region.name,
				root + '/' + loc.region.slug
			]);
		}

		if (loc.subterritory && loc.subterritory.id) {
			breadcrumbs.push([
				(loc.subterritory.name + ' ' + (loc.subterritory.label || '')).trim(),
				root + '/' + loc.subterritory.slug
			]);
		}

		if (loc.locality && loc.locality.id) {
			breadcrumbs.push([
				loc.locality.name,
				root + '/' + loc.locality.slug
			]);
		}

		if (loc.postal_code) {
			breadcrumbs.push([
				loc.postal_code.name,
				root + '/' + loc.postal_code.slug
			]);
		}

		return breadcrumbs;
	},

	/**
	 * Load view
	 *
	 * @param {String} module
	 * @param {String} view
	 * @param {Function} fn
	 */
	view(module, view, fn) {
		$.fetch.request({
			url: `/assets/modules/${module}@${$.get('versions.' + module)}/views/${view}.html`,
			success: fn
		});
	},

	/**
	 * Get interest rates
	 *
	 * @param {Boolean} [structure=false]
	 * @returns {Object}
	 */
	getRates(structure) {
		if (structure) {
			return {
				120: 5.6,
				180: 5.8,
				240: 6,
				360: 6.2
			};
		}

		return {
			12: 6.2,
			36: 6.4,
			60: 6.6,
			84: 6.8,
			120: 7,
			180: 7.2,
			240: 7.4,
			360: 7.6
		};
	}
};